import {
  FC,
  useCallback,
  ChangeEvent,
  useState,
  DragEvent,
  MouseEvent,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import styled, { css } from "styled-components";
import { isObject } from "lodash";

import { Label } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { SrOnly } from "@/components/Layout/Parts";
import { ReactComponent as ImgIcon } from "@/assets/icons/img-placeholder.svg";
import Text from "@/components/Text";
import { MediaResource } from "@/types/resources";
import { uploadFileToS3 } from "@/utils/s3";
import useAlerts from "@/hooks/useAlerts";
import ProgressBar from "@/components/ProgressBar";

interface DropzoneProps {
  $dragging: boolean;
}

const Dropzone = styled.div<DropzoneProps>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

export const Title = styled(Text)`
  margin: 10px 0 6px;
`;

export const Notice = styled(Text)`
  opacity: 0.5;
  text-align: center;
`;

const Preview = styled.div`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
`;

const Img = styled.img`
  max-width: 128px;
  height: auto;
  margin-right: 16px;
  object-fit: cover;
`;

const FileInfo = styled.div`
  flex: 1;
  text-align: left;
  padding-left: 16px;
  overflow-x: hidden;
`;

const FileName = styled(Text)`
  margin-bottom: 8px;
  text-overflow: ellipsis;
  overflow-x: hidden;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

const InnerLabel = styled(Text)`
  cursor: pointer;
`;

const StyledProgressBar = styled(ProgressBar)`
  height: 8px;
`;

const MultipleProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const validImageTypes = [
  "image/png",
  "image/gif",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
];

interface Props {
  pathName?: string;
  fileName?: string;
  label?: string;
  existingMedia?: MediaResource;
  accept?: string;
  notes?: string;
  className?: string;
  multiple?: boolean;
  fieldsIndex?: number;
  maxFileSizeInMB?: number;
  id: string;
  onUploaded: (
    images: Array<{ path?: string; file: File }>,
    fieldsIndex?: number
  ) => void;
  onDelete?: () => void;
}

const ImageField: FC<Props> = ({
  pathName = "image",
  fileName = "file",
  label,
  accept = "image/*",
  notes = "JPG, PNG, GIF, SVG up to 2MB",
  maxFileSizeInMB = 2,
  fieldsIndex,
  className,
  id,
  multiple = false,
  onDelete,
  onUploaded,
}) => {
  const alerts = useAlerts();

  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProggress, setUploadProgress] = useState<
    | number
    | {
        [key: number]: number;
      }
  >(0);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const ensureFileIsValid = useCallback(
    (file: File) => {
      if (!validImageTypes.includes(file.type)) {
        return false;
      }

      if (file.size > maxFileSizeInMB * 1024 * 1024) {
        return false;
      }

      return true;
    },
    [maxFileSizeInMB]
  );

  const onBulkUpload = useCallback(
    async (files: FileList) => {
      if (!multiple) {
        if (!ensureFileIsValid(files[0])) {
          return alerts.error(
            `Image ${files[0].name} is not valid!`,
            "Make sure that this file is of type image, max size: 2MB"
          );
        }

        setUploadProgress(0);
        setIsUploading(true);

        const path = await uploadFileToS3(
          files[0]!,
          (progressEvent: ProgressEvent) =>
            setUploadProgress(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            )
        );

        setIsUploading(false);

        return onUploaded([{ file: files[0]!, path }], fieldsIndex);
      }

      setUploadProgress(
        Array.from(files).reduce((final, _, i) => ({ ...final, [i]: 0 }), {})
      );
      setIsUploading(true);

      const data = await Promise.all(
        Array.from(files)
          .filter(ensureFileIsValid)
          .map((file, i) =>
            uploadFileToS3(file, (progressEvent: ProgressEvent) =>
              setUploadProgress((prev) => ({
                ...(prev as object),
                [i!]: Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100
                ),
              }))
            ).then((path) => ({
              file,
              path,
            }))
          )
      );

      setIsUploading(false);

      onUploaded(data, fieldsIndex);
    },
    [fieldsIndex, onUploaded, multiple, ensureFileIsValid, alerts]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) {
        return;
      }
      onBulkUpload(e.target.files!);
    },
    [onBulkUpload]
  );

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (!e.dataTransfer?.files?.length) {
      return;
    }

    onBulkUpload(e.dataTransfer.files);
    e.dataTransfer.clearData();
  };

  const onClearImage = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (onDelete) {
      return onDelete();
    }

    setValue(pathName, "");
    setValue(fileName, null);
  };

  const file = useWatch({ control, name: fileName }) as File | null;

  const previewable = file;

  const src = file ? URL.createObjectURL(file) : "";

  const renderUploadProgress = () => {
    if (!isObject(uploadProggress)) {
      return (
        <StyledProgressBar
          progress={uploadProggress as number}
          color="primary"
          showPercentage
        />
      );
    }

    return (
      <MultipleProgressWrap>
        {Object.keys(uploadProggress).map((key) => (
          <div key={key}>
            <StyledProgressBar
              progress={
                uploadProggress[key as unknown as keyof typeof uploadProggress]
              }
              color="primary"
              showPercentage
            />
          </div>
        ))}
      </MultipleProgressWrap>
    );
  };

  return (
    <div className={className}>
      {!!label && <Label>{label}</Label>}
      {isUploading ? (
        renderUploadProgress()
      ) : (
        <>
          {previewable ? (
            <Preview>
              <ClearButton onClick={onClearImage}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 12L4 4"
                    stroke="#222222"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ClearButton>
              <Img src={src} alt="" />
              <FileInfo>
                <FileName as="div" $type="smallMedium">
                  {previewable.name}
                </FileName>
                <Text as="div" $type="smallMedium" $color="gray">
                  {Math.floor(previewable.size / 1000)}Kb
                </Text>
              </FileInfo>
            </Preview>
          ) : (
            <Dropzone
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              $dragging={dragging}
            >
              <ImgIcon />
              <Title $type="smallBold" $color="gray" as="div">
                Drop image{multiple && "s"} here or{" "}
                <InnerLabel
                  as="label"
                  $type="smallBold"
                  $color="purple"
                  htmlFor={id}
                >
                  browse
                  <SrOnly
                    as="input"
                    type="file"
                    accept={accept}
                    id={id}
                    multiple={multiple}
                    onChange={onChange}
                  />
                </InnerLabel>
              </Title>
              <Notice $type="extraSmall" $color="gray" as="div">
                Supports: <span dangerouslySetInnerHTML={{ __html: notes }} />
              </Notice>
            </Dropzone>
          )}
        </>
      )}
      {!!errors[pathName] && (
        <ValidationMessage>
          {errors[pathName]?.message as string}
        </ValidationMessage>
      )}
    </div>
  );
};

export default ImageField;
